import LimitsDelegate from '../delegates/Limits.delegate';
import TabController from './Tab.controller';
import Inherit from '../../../core/inherit';
import {
    ACCESS_RULES_PERSIST_ENABLED,
    DEFAULT_ACCESS_ENABLED,
    DEFAULT_ACCESS_RULES,
    DEFAULT_LIMITS_ENABLED,
    DEFAULT_LIMITS_STATE,
    DEFAULT_LIMIT_POLICY
} from '../configuration.constants';
import { clone } from '../../../utils/json';

/**
 * View-model for the "Limits" tab. Its render slice lives in the host
 * component's `state.tabs.limits` (via {@link TabController}).
 *
 * Owns the `limit_policy` form, the `limits_enabled` section toggles and
 * the access-rules side panel state. Access rules round-trip to snake_case
 * but are read-only in the UI until {@link ACCESS_RULES_PERSIST_ENABLED}
 * is true and `getValues()` includes them in the payload.
 */
class LimitsController extends Inherit(TabController, LimitsDelegate) {
    constructor(page) {
        super(page, 'limits');
        this.snapshot = null;
    }

    /** Default render slice the host seeds `state.tabs.limits` with. */
    getInitialState() {
        return clone(DEFAULT_LIMITS_STATE);
    }

    // --- Snapshot / change detection -----------------------------------

    getAccessRules(includeAccess = false) {
        const persistAccess = includeAccess && ACCESS_RULES_PERSIST_ENABLED;
        return persistAccess
            ? {
                  access_rules: this.state.access_rules,
                  access_enabled: this.state.access_enabled
              }
            : {
                  access_rules: this.snapshot?.access_rules ?? this.state.access_rules,
                  access_enabled: this.snapshot?.access_enabled ?? this.state.access_enabled
              };
    }

    /**
     * @param {{ includeAccess?: boolean }} [options]
     * When access rules are not persisted, post-save snapshots keep the last
     * loaded access baseline so the UI does not go "clean" for unsaved edits.
     */
    captureSnapshot({ includeAccess = true } = {}) {
        const access = this.getAccessRules(includeAccess);
        this.snapshot = clone({
            limit_policy: this.state.limit_policy,
            limits_enabled: this.state.limits_enabled,
            ...access
        });
    }

    hasChanges() {
        if (!this.snapshot) return false;
        if (JSON.stringify(this.state.limit_policy) !== JSON.stringify(this.snapshot.limit_policy)) return true;
        if (JSON.stringify(this.state.limits_enabled) !== JSON.stringify(this.snapshot.limits_enabled)) return true;
        if (ACCESS_RULES_PERSIST_ENABLED) {
            if (JSON.stringify(this.state.access_rules) !== JSON.stringify(this.snapshot.access_rules)) return true;
            if (JSON.stringify(this.state.access_enabled) !== JSON.stringify(this.snapshot.access_enabled)) return true;
        }
        return false;
    }

    // --- Transforms -----------------------------------------------------

    transformPolicyToSnakeCase(limitPolicy = {}) {
        const transformed = {
            max_advances: limitPolicy.maxAdvances ?? null,
            advance_timeframe: limitPolicy.advanceTimeframe || null,
            min_advance_amount: limitPolicy.minAdvanceAmount ?? null,
            max_advance_amount: limitPolicy.maxAdvanceAmount ?? null,
            max_advance_volume: limitPolicy.maxAdvanceVolume ?? null,
            fee_cap: limitPolicy.feeCap ?? null
        };

        if (limitPolicy.advanceCapTenureRules?.length) {
            transformed.advance_cap_tenure_rules = limitPolicy.advanceCapTenureRules.map(r => ({
                id: r.id,
                from_days: r.fromDays,
                to_days: r.toDays,
                value: r.value,
                type: r.type || 'exactly'
            }));
        } else {
            transformed.advance_cap_tenure_rules = [];
        }

        if (limitPolicy.volumeCapTenureRules?.length) {
            transformed.volume_cap_tenure_rules = limitPolicy.volumeCapTenureRules.map(r => ({
                id: r.id,
                from_days: r.fromDays,
                to_days: r.toDays,
                value: r.value,
                type: r.type || 'exactly'
            }));
        } else {
            transformed.volume_cap_tenure_rules = [];
        }

        return transformed;
    }

    transformPolicyToCamelCase(limitPolicy = {}) {
        const transformed = {
            maxAdvances: limitPolicy.max_advances ?? DEFAULT_LIMIT_POLICY.maxAdvances,
            advanceTimeframe: limitPolicy.advance_timeframe || DEFAULT_LIMIT_POLICY.advanceTimeframe,
            minAdvanceAmount: limitPolicy.min_advance_amount ?? DEFAULT_LIMIT_POLICY.minAdvanceAmount,
            maxAdvanceAmount: limitPolicy.max_advance_amount ?? DEFAULT_LIMIT_POLICY.maxAdvanceAmount,
            maxAdvanceVolume: limitPolicy.max_advance_volume ?? DEFAULT_LIMIT_POLICY.maxAdvanceVolume,
            feeCap: limitPolicy.fee_cap ?? DEFAULT_LIMIT_POLICY.feeCap
        };

        if (limitPolicy.advance_cap_tenure_rules?.length) {
            transformed.advanceCapTenureRules = limitPolicy.advance_cap_tenure_rules.map((r, idx) => ({
                id: r.id || idx,
                fromDays: r.from_days ?? 0,
                toDays: r.to_days ?? 36500,
                value: r.value ?? 0,
                type: r.type || 'exactly'
            }));
        } else {
            transformed.advanceCapTenureRules = [];
        }

        if (limitPolicy.volume_cap_tenure_rules?.length) {
            transformed.volumeCapTenureRules = limitPolicy.volume_cap_tenure_rules.map((r, idx) => ({
                id: r.id || idx,
                fromDays: r.from_days ?? 0,
                toDays: r.to_days ?? 36500,
                value: r.value ?? 0,
                type: r.type || 'exactly'
            }));
        } else {
            transformed.volumeCapTenureRules = [];
        }

        return transformed;
    }

    // Round-trip is wired so the day the backend exposes `access_rules`
    // we just have to add it to the submit payload. Until then the submit
    // payload omits it (see `handleSubmit`).
    transformAccessRulesToSnakeCase(accessRules = {}) {
        return {
            negative_balance_days: accessRules?.negativeBalanceDays ?? null,
            overdraft_nsf_days: accessRules?.overdraftNsfDays ?? null,
            loans_overdue_days: accessRules?.loansOverdueDays ?? null
        };
    }

    transformAccessRulesToCamelCase(accessRules = {}) {
        return {
            negativeBalanceDays: accessRules?.negative_balance_days ?? DEFAULT_ACCESS_RULES.negativeBalanceDays,
            overdraftNsfDays: accessRules?.overdraft_nsf_days ?? DEFAULT_ACCESS_RULES.overdraftNsfDays,
            loansOverdueDays: accessRules?.loans_overdue_days ?? DEFAULT_ACCESS_RULES.loansOverdueDays
        };
    }

    // --- Lifecycle (orchestrator API) ----------------------------------

    /**
     * Pure: computes and returns this tab's initial slice from the FI
     * payload without touching React state. Seeds the snapshot.
     */
    applyFromFi(fiData) {
        const next = this.getInitialState();

        if (fiData?.limit_policy) {
            next.limit_policy = this.transformPolicyToCamelCase(fiData.limit_policy);
        }
        if (fiData?.limits_enabled) {
            next.limits_enabled = {
                maxAdvances: fiData.limits_enabled.maxAdvances ?? DEFAULT_LIMITS_ENABLED.maxAdvances,
                advanceAmounts: fiData.limits_enabled.advanceAmounts ?? DEFAULT_LIMITS_ENABLED.advanceAmounts,
                totalVolume: fiData.limits_enabled.totalVolume ?? DEFAULT_LIMITS_ENABLED.totalVolume,
                feeCap: fiData.limits_enabled.feeCap ?? DEFAULT_LIMITS_ENABLED.feeCap
            };
        }
        if (fiData?.access_rules) {
            next.access_rules = this.transformAccessRulesToCamelCase(fiData.access_rules);
        }
        if (fiData?.access_enabled) {
            next.access_enabled = {
                negativeBalance: fiData.access_enabled.negativeBalance ?? DEFAULT_ACCESS_ENABLED.negativeBalance,
                overdraftNsf: fiData.access_enabled.overdraftNsf ?? DEFAULT_ACCESS_ENABLED.overdraftNsf,
                loansOverdue: fiData.access_enabled.loansOverdue ?? DEFAULT_ACCESS_ENABLED.loansOverdue
            };
        }

        this.snapshot = clone({
            limit_policy: next.limit_policy,
            limits_enabled: next.limits_enabled,
            access_rules: next.access_rules,
            access_enabled: next.access_enabled
        });
        return next;
    }

    /**
     * Returns the slice the orchestrator drops into the submit-all payload.
     * Access rules are intentionally omitted until the backend contract
     * is defined.
     */
    getValues() {
        const payload = {
            limit_policy: this.transformPolicyToSnakeCase(this.state.limit_policy),
            limits_enabled: this.state.limits_enabled
        };
        if (ACCESS_RULES_PERSIST_ENABLED) {
            payload.access_rules = this.transformAccessRulesToSnakeCase(this.state.access_rules);
            payload.access_enabled = this.state.access_enabled;
        }
        return payload;
    }

    // --- Submit / cancel -----------------------------------------------

    validateLimitPolicy() {
        const { limit_policy = {}, limits_enabled = {} } = this.state;
        const required = [
            { key: 'minAdvanceAmount', label: 'Min Advance Amount', section: 'advanceAmounts' },
            { key: 'maxAdvanceAmount', label: 'Max Advance Amount', section: 'advanceAmounts' },
            { key: 'maxAdvanceVolume', label: 'Max Advance Volume', section: 'totalVolume' },
        ];
        for (const { key, label, section } of required) {
            if (limits_enabled[section] && (limit_policy[key] === null || limit_policy[key] === undefined)) {
                return `${label} is required when the section is enabled.`;
            }
        }
        return null;
    }

    async handleSubmit() {
        if (!this.page.canModifyConfiguration()) {
            return;
        }
        try {
            const validationError = this.validateLimitPolicy();
            if (validationError) {
                this.page.props.notificationApi.error({
                    message: 'Validation Error',
                    description: validationError
                });
                return;
            }

            this.page.setLoading(true);
            const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
            if (!aba) {
                this.page.props.notificationApi.error({
                    message: 'Error',
                    description: 'Unable to find FI ABA'
                });
                return;
            }

            const response = await this.page.data.updateFI(aba, { fi_info: this.getValues() });

            if (response) {
                this.captureSnapshot({ includeAccess: ACCESS_RULES_PERSIST_ENABLED });
                if (!ACCESS_RULES_PERSIST_ENABLED) {
                    this.setState({
                        access_rules: clone(this.snapshot.access_rules),
                        access_enabled: clone(this.snapshot.access_enabled)
                    });
                }
                this.page.props.notificationApi.success({
                    message: 'Success',
                    description: ACCESS_RULES_PERSIST_ENABLED
                        ? 'Limits configuration updated successfully'
                        : 'Advance limits updated successfully'
                });
            }
        } catch (error) {
            console.error('Error updating limits:', error);
            this.page.props.notificationApi.error({
                message: 'Update Failed',
                description: 'Failed to update limits configuration'
            });
        } finally {
            this.page.setLoading(false);
        }
    }

    handleCancel() {
        if (!this.snapshot) return;
        this.replaceState(clone(this.snapshot));
    }

    // --- Tenure rule helpers ------------------------------------------------

    setLimitPolicyField(key, value) {
        const next = clone(this.state.limit_policy);
        next[key] = value;
        this.setState({ limit_policy: next });
    }

    setLimitsEnabledField(key, value) {
        const next = clone(this.state.limits_enabled);
        next[key] = value;
        this.setState({ limits_enabled: next });
    }

    toggleLimitsSection(section) {
        const next = clone(this.state.limits_enabled);
        next[section] = !next[section];
        this.setState({ limits_enabled: next });
    }

    setAccessRulesField(key, value) {
        const next = clone(this.state.access_rules);
        next[key] = value;
        this.setState({ access_rules: next });
    }

    toggleAccessRule(key) {
        const next = clone(this.state.access_enabled);
        next[key] = !next[key];
        this.setState({ access_enabled: next });
    }

    addAdvanceCapTenureRule() {
        const next = clone(this.state.limit_policy);
        if (!next.advanceCapTenureRules) next.advanceCapTenureRules = [];
        const newId = Math.max(0, ...next.advanceCapTenureRules.map(r => r.id || 0)) + 1;
        next.advanceCapTenureRules.push({
            id: newId,
            fromDays: 0,
            toDays: 30,
            value: 5,
            type: 'exactly'
        });
        this.setState({ limit_policy: next });
    }

    removeAdvanceCapTenureRule(ruleId) {
        const next = clone(this.state.limit_policy);
        next.advanceCapTenureRules = next.advanceCapTenureRules.filter(r => r.id !== ruleId);
        this.setState({ limit_policy: next });
    }

    updateAdvanceCapTenureRule(ruleId, field, value) {
        const next = clone(this.state.limit_policy);
        const rule = next.advanceCapTenureRules.find(r => r.id === ruleId);
        if (rule) {
            rule[field] = value;
        }
        this.setState({ limit_policy: next });
    }

    addVolumeCapTenureRule() {
        const next = clone(this.state.limit_policy);
        if (!next.volumeCapTenureRules) next.volumeCapTenureRules = [];
        const newId = Math.max(0, ...next.volumeCapTenureRules.map(r => r.id || 0)) + 1;
        next.volumeCapTenureRules.push({
            id: newId,
            fromDays: 0,
            toDays: 30,
            value: 500.00,
            type: 'exactly'
        });
        this.setState({ limit_policy: next });
    }

    removeVolumeCapTenureRule(ruleId) {
        const next = clone(this.state.limit_policy);
        next.volumeCapTenureRules = next.volumeCapTenureRules.filter(r => r.id !== ruleId);
        this.setState({ limit_policy: next });
    }

    updateVolumeCapTenureRule(ruleId, field, value) {
        const next = clone(this.state.limit_policy);
        const rule = next.volumeCapTenureRules.find(r => r.id === ruleId);
        if (rule) {
            rule[field] = value;
        }
        this.setState({ limit_policy: next });
    }

    // --- View props -----------------------------------------------------

    getViewProps() {
        const { limit_policy = {}, limits_enabled = {}, access_rules = {}, access_enabled = {} } = this.state;
        const { firstTimeSetup, loading, canModify } = this.page.state;

        const handlers = {
            setMaxAdvances: (v) => this.setLimitPolicyField('maxAdvances', v === null || v === undefined ? null : v),
            setAdvanceTimeframe: (v) => this.setLimitPolicyField('advanceTimeframe', v),
            setMinAdvanceAmount: (v) => this.setLimitPolicyField('minAdvanceAmount', v),
            setMaxAdvanceAmount: (v) => this.setLimitPolicyField('maxAdvanceAmount', v),
            setMaxAdvanceVolume: (v) => this.setLimitPolicyField('maxAdvanceVolume', v),
            setFeeCap: (v) => this.setLimitPolicyField('feeCap', v === null || v === undefined ? null : v),
            toggleLimitsSection: (k) => this.toggleLimitsSection(k),
            toggleAccessRule: (k) => this.toggleAccessRule(k),
            updateAccessRuleDays: (k, v) => this.setAccessRulesField(k, v === null || v === undefined ? null : v),
            addAdvanceCapTenureRule: this.addAdvanceCapTenureRule.bind(this),
            removeAdvanceCapTenureRule: this.removeAdvanceCapTenureRule.bind(this),
            updateAdvanceCapTenureRule: this.updateAdvanceCapTenureRule.bind(this),
            addVolumeCapTenureRule: this.addVolumeCapTenureRule.bind(this),
            removeVolumeCapTenureRule: this.removeVolumeCapTenureRule.bind(this),
            updateVolumeCapTenureRule: this.updateVolumeCapTenureRule.bind(this)
        };

        return {
            values: {
                maxAdvances: limit_policy.maxAdvances,
                advanceTimeframe: limit_policy.advanceTimeframe,
                minAdvanceAmount: limit_policy.minAdvanceAmount,
                maxAdvanceAmount: limit_policy.maxAdvanceAmount,
                maxAdvanceVolume: limit_policy.maxAdvanceVolume,
                feeCap: limit_policy.feeCap,
                advanceCapTenureRules: limit_policy.advanceCapTenureRules || [],
                volumeCapTenureRules: limit_policy.volumeCapTenureRules || []
            },
            enabled: limits_enabled,
            handlers,
            // Slice the access-rules side card consumes (kept the same
            // shape so the dumb card doesn't change).
            accessRulesCardData: { access_rules, access_enabled },
            accessRulesReadOnly: !ACCESS_RULES_PERSIST_ENABLED || !canModify,
            loading,
            readOnly: !canModify,
            disabledSave: !canModify || (!firstTimeSetup && !this.hasChanges()),
            submitText: firstTimeSetup ? 'Submit All' : 'Save',
            confirmBeforeSubmit: !firstTimeSetup,
            onSubmit: firstTimeSetup
                ? this.page.handleContinue.bind(this.page)
                : this.handleSubmit.bind(this),
            onCancel: firstTimeSetup
                ? this.page.handleCancel.bind(this.page)
                : this.handleCancel.bind(this)
        };
    }
}

export default LimitsController;
